import {
  ID,
  QueryFilterOptionKeys,
  QueryFilterOptions,
  Task,
  TaskFilterOptions,
  TaskPriorityOptions,
  TaskType,
} from '@griply/types';
import { chunkArray, isNumber } from '@griply/utils';
import { useQuery } from '@tanstack/react-query';
import { Query } from 'firebase/firestore';
import { useMemo } from 'react';
import { createSubscriptionQuery } from 'shared/lib/@tanstack-query';
import { getFilteredTasks } from 'shared/services/backend-api';

export const queryKey = 'filtered-tasks';

export const useFilteredTasksQuery = (
  uid: ID | undefined,
  filters: TaskFilterOptions | TaskFilterOptions[],
  enabled = true,
) => {
  const filtersArray = useMemo(
    () => (Array.isArray(filters) ? filters : [filters]),
    [filters],
  );

  // We need to generate a list of all query configurations across all filter sets
  const allQueryConfigs = useMemo(() => {
    return filtersArray.flatMap((filterSet) => {
      const keys = Object.values(QueryFilterOptionKeys);
      const queryFilters = keys.reduce<QueryFilterOptions>((acc, key) => {
        const _key = key as QueryFilterOptionKeys;
        if (
          _key === QueryFilterOptionKeys.NoDeadline ||
          _key === QueryFilterOptionKeys.NoDeadlineDeadline ||
          _key === QueryFilterOptionKeys.HasDeadline ||
          _key === QueryFilterOptionKeys.HasDeadlineDeadline ||
          _key === QueryFilterOptionKeys.ParentIds ||
          _key === QueryFilterOptionKeys.NoParentIds
        ) {
          return acc;
        }

        const val = filterSet[_key] as QueryFilterOptions[typeof _key];
        if (val) {
          (
            acc as Record<
              QueryFilterOptionKeys,
              QueryFilterOptions[keyof QueryFilterOptions]
            >
          )[_key] = val;
        }
        return acc;
      }, {});

      if (!filterSet.types?.length || filterSet.types.includes(TaskType.Task)) {
        queryFilters.tasksOnly = true;
      }
      return { taskFilter: filterSet, queryConfig: queryFilters };
    });
  }, [filtersArray]);

  const queryResult = useQuery({
    queryKey: [
      queryKey,
      uid ?? '',
      enabled.toString(),
      // Include all filter permutations in key
      ...allQueryConfigs.flatMap(({ queryConfig }) =>
        Object.entries(queryConfig)
          .filter(([, value]) => value !== undefined)
          .map(
            ([key, value]) =>
              `${key}: ${
                value instanceof Date
                  ? value.getTime().toString()
                  : Array.isArray(value)
                    ? value.toSorted().join(',')
                    : value
              }`,
          ),
      ),
    ],
    queryFn: (context) => {
      if (!enabled || !uid) {
        return [];
      }

      const allQueries: (() => Promise<Query<Task>>)[] = [];

      allQueryConfigs.forEach(({ taskFilter, queryConfig }) => {
        if (
          taskFilter.types?.length &&
          !taskFilter.types.includes(TaskType.Task)
        ) {
          return;
        }

        const {
          goals,
          lifeAreas,
          ids,
          noLifeArea,
          noGoal,
          tasksOnly,
          ...rest
        } = queryConfig;
        // filtering on ids without any ids present means no tasks
        if (ids && !ids.length) {
          return;
        }

        const splitQueries =
          [goals?.length, lifeAreas?.length, ids?.length].filter(Boolean)
            .length > 1 ||
          (lifeAreas?.length && noLifeArea) ||
          (goals?.length && noGoal) ||
          (goals?.length ?? 0) > 30 ||
          (lifeAreas?.length ?? 0) > 30 ||
          (ids?.length ?? 0) > 30;

        if (!splitQueries) {
          allQueries.push(() =>
            getFilteredTasks(uid, {
              ...queryConfig,
              tasksOnly: tasksOnly ?? true,
            }),
          );
        } else {
          // Chunking logic
          if (lifeAreas?.length) {
            chunkArray(lifeAreas, 30).forEach((chunked) =>
              allQueries.push(() =>
                getFilteredTasks(uid, {
                  ...rest,
                  lifeAreas: chunked,
                  tasksOnly: tasksOnly ?? true,
                }),
              ),
            );
          }
          if (noLifeArea) {
            allQueries.push(() =>
              getFilteredTasks(uid, {
                ...rest,
                noLifeArea: true,
                tasksOnly: tasksOnly ?? true,
              }),
            );
          }
          if (goals?.length) {
            chunkArray(goals, 30).forEach((chunked) =>
              allQueries.push(() =>
                getFilteredTasks(uid, {
                  ...rest,
                  goals: chunked,
                  tasksOnly: tasksOnly ?? true,
                }),
              ),
            );
          }
          if (noGoal) {
            allQueries.push(() =>
              getFilteredTasks(uid, {
                ...rest,
                noGoal: true,
                tasksOnly: tasksOnly ?? true,
              }),
            );
          }
          if (ids?.length) {
            chunkArray(ids, 30).forEach((chunked) =>
              allQueries.push(() =>
                getFilteredTasks(uid, {
                  ...rest,
                  ids: chunked,
                  tasksOnly: tasksOnly ?? true,
                }),
              ),
            );
          }
        }
      });

      if (!allQueries.length) {
        return [];
      }

      return createSubscriptionQuery(allQueries)(context);
    },
    placeholderData: (previous) => previous ?? [],
    enabled: !!uid && enabled,
  });

  const processedData = useMemo(() => {
    // Deduplicate data from multiple queries based on ID
    const rawData = queryResult.data ?? [];
    // Ensure unique tasks by ID
    const uniqueDataMap = new Map<ID, Task>();
    rawData.forEach((item) => {
      if (item?.id) {
        uniqueDataMap.set(item.id, item);
      }
    });
    const uniqueData = Array.from(uniqueDataMap.values());

    return uniqueData.filter((task) =>
      // Task must match AT LEAST ONE of the filter sets
      filtersArray.some((filters) => {
        if (
          (filters.deleted && !task.deletedAt) ||
          (!filters.deleted && !!task.deletedAt)
        ) {
          return false;
        }

        if (
          !(
            filters.ids?.length ||
            filters.goals?.length ||
            filters.noGoal ||
            filters.lifeAreas?.length ||
            filters.noLifeArea ||
            isNumber(filters.minDuration) ||
            isNumber(filters.maxDuration) ||
            filters.priorities?.length ||
            filters.excludeIds?.length ||
            !filters.includeTaskWithAction ||
            filters.noDeadline ||
            filters.noDeadlineDeadline ||
            filters.hasDeadline ||
            filters.hasDeadlineDeadline ||
            filters.parentIds?.length ||
            (filters.noParentIds ?? true) ||
            filters.archivedGoalIds?.length ||
            filters.deleted !== undefined
          )
        ) {
          return true;
        }

        if (
          filters.priorities?.length &&
          !(
            !task.priority &&
            filters.priorities.includes(TaskPriorityOptions.None)
          ) &&
          !filters.priorities.includes(task.priority!)
        ) {
          return false;
        }

        if (filters.ids && !filters.ids.includes(task.id)) {
          return false;
        }

        if (filters.noLifeArea && task.lifeAreaId) {
          return false;
        }

        if (filters.noGoal && task.goalId) {
          return false;
        }

        if (filters.noDeadline && task.endStrategy?.deadline) {
          return false;
        }

        if (filters.hasDeadline && !task.endStrategy?.deadline) {
          return false;
        }

        if (filters.noDeadlineDeadline && task.deadlineDeadline) {
          return false;
        }

        if (filters.hasDeadlineDeadline && !task.deadlineDeadline) {
          return false;
        }

        if (
          filters.parentIds?.length &&
          !task.parentIds?.some((parentId) =>
            filters.parentIds!.includes(parentId),
          )
        ) {
          return false;
        }

        const applyNoParentIds =
          (filters.noParentIds ?? true) && !filters.parentIds?.length;

        if (applyNoParentIds && !!task.parentIds?.length) {
          return false;
        }

        const linkedItemIds =
          filters.lifeAreas?.length || filters.goals?.length
            ? [...(filters.lifeAreas ?? []), ...(filters.goals ?? [])]
            : undefined;

        if (
          linkedItemIds &&
          !(
            (task.lifeAreaId && linkedItemIds.includes(task.lifeAreaId)) ||
            (task.goalId && linkedItemIds.includes(task.goalId))
          )
        ) {
          return false;
        }

        if (
          isNumber(filters.minDuration) &&
          (!task.timeslot?.duration ||
            task.timeslot.duration < filters.minDuration)
        ) {
          return false;
        }

        if (
          isNumber(filters.maxDuration) &&
          (!task.timeslot?.duration ||
            task.timeslot.duration > filters.maxDuration)
        ) {
          return false;
        }

        if (
          filters.excludeIds?.length &&
          filters.excludeIds.includes(task.id)
        ) {
          return false;
        }

        if (
          filters.archivedGoalIds?.length &&
          task.goalId &&
          filters.archivedGoalIds.includes(task.goalId)
        ) {
          return false;
        }

        if (!filters.includeTaskWithAction && task.action) {
          return false;
        }

        if (task.completedAt) {
          if (
            filters.completedStartDate !== undefined &&
            task.completedAt < filters.completedStartDate
          ) {
            return false;
          }
          if (
            filters.completedEndDate !== undefined &&
            task.completedAt > filters.completedEndDate
          ) {
            return false;
          }
        }

        return true;
      }),
    );
  }, [queryResult.data, filtersArray]);

  return {
    ...queryResult,
    data: processedData,
  };
};
